import { memo, useEffect, useMemo, useRef } from 'react'
import { useInView } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { usePromoteInboundSales } from '@/features/priceCalculator/PurchaseForm/priceCalculatorAtoms'
import { logger } from '@/services/logger/logger.client'
import { useTracking } from '@/services/Tracking/useTracking'
import { Features } from '@/utils/Features'
import { Button } from 'ui/src/components/Button/Button'
import { ChevronIcon } from 'ui/src/icons/Chevron'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { useActiveFormSectionId } from '../../useActiveFormSectionId'
import { usePriceCalculatorForm } from '../../usePriceCalculatorForm'
import { usePriceCalculatorStep } from '../../usePriceCalculatorStep'
import { usePriceIntent } from '../../usePriceIntent'
import { InboundSalesCard } from '../InboundSalesCard/InboundSalesCard'
import { useOfferVariants } from '../useOfferVariants'
import { useSelectedOffer } from '../useSelectedOffer'
import { CarAddonsSelector } from './CarAddonsSelector/CarAddonsSelector'
import { TravelAddonsSelector } from './TravelAddonSelector/TravelAddonsSelector'
import { DeductibleSection } from './DeductibleSection'
import { InsuranceAmountSection } from './InsuranceAmountSection'
import { OfferSummary } from './OfferSummary'
import { TiersSection } from './TierSection'
import { sectionsWrapper } from './OfferPresenter.css'

export const OfferPresenter = memo(() => {
  const { t } = useTranslation('purchase-form')
  const [selectedOffer, setSelectedOffer] = useSelectedOffer()
  const { priceIntent } = usePriceIntent()
  const { setStep } = usePriceCalculatorStep()
  const { setActiveFormSectionId } = useActiveFormSectionId()
  const form = usePriceCalculatorForm()

  if (!priceIntent) {
    throw new Error('priceIntent must be defined')
  }

  if (selectedOffer == null) {
    throw new Error('selectedOffer must be defined')
  }

  const { tiers, deductibles, insuranceAmounts } = useOfferVariants({
    offers: priceIntent.offers,
    selectedOffer,
  })

  const handleOfferChange = (offerId: string) => {
    const offer = priceIntent.offers.find((offer) => offer.id === offerId)

    if (offer === undefined) {
      logger.error(`Unknown offer selected: ${offerId}`)
      return
    }

    setSelectedOffer(offer)
  }

  const handleEditInformation = () => {
    setStep('fillForm')
    // Make sure to always go to the last section of the form when editing
    const lastFormSectionId = form.sections[form.sections.length - 1].id
    setActiveFormSectionId(lastFormSectionId)
    // Make sure the user is viewing the start of the form when editing
    window.scrollTo({ top: 0, behavior: 'instant' })
  }

  const tracking = useTracking()
  const offerRef = useRef(null)
  const offerIsInView = useInView(offerRef, { once: true })

  useEffect(() => {
    if (offerIsInView) {
      tracking.reportViewItem(selectedOffer, 'store')
    }
  }, [selectedOffer, tracking, offerIsInView])

  const hasTravelAddons = selectedOffer.addons.some((addon) => addon.variant.product === 'SE_TRAVEL_ADDON')

  // Car addons needs different UI from the Home travel addons
  const isCarProduct = selectedOffer.product.name === 'SE_CAR'
  const showCarAddons = Features.enabled('CAR_ADDONS') && isCarProduct
  const promoteInboundSales = usePromoteInboundSales()

  const selectedTier = useMemo(() => {
    const tier = tiers.find((item) => item.variant.typeOfContract === selectedOffer.variant.typeOfContract)

    if (tier === undefined) {
      logger.warn(`Unknown tier selected`, {
        selectedOffer: selectedOffer.variant.typeOfContract,
        tiers: tiers.map((item) => item.variant.typeOfContract),
      })
      return tiers[0]
    }

    return tier
  }, [tiers, selectedOffer])

  return (
    <div className={yStack({ gap: { _: 'lg', lg: 9 } })} ref={offerRef}>
      <Button
        className={sprinkles({ marginRight: 'auto' })}
        size="small"
        variant="secondary"
        Icon={<ChevronIcon size="0.75rem" direction="left" />}
        onClick={handleEditInformation}
      >
        {t('OFFER_PRESENTER_EDIT_INFO_BUTTON_LABEL')}
      </Button>

      <div className={sectionsWrapper}>
        <TiersSection tiers={tiers} selectedTier={selectedTier} onTierChange={handleOfferChange} />

        {deductibles.length > 1 && (
          <DeductibleSection
            deductibles={deductibles}
            selectedDeductibleId={selectedOffer.id}
            onDeductibleChange={handleOfferChange}
          />
        )}

        {insuranceAmounts.length > 1 && (
          <InsuranceAmountSection
            insuranceAmounts={insuranceAmounts}
            selectedInsuranceAmountId={selectedOffer.id}
            onInsuranceAmountChange={handleOfferChange}
          />
        )}

        {hasTravelAddons && <TravelAddonsSelector addons={selectedOffer.addons} />}

        {showCarAddons && <CarAddonsSelector offer={selectedOffer} />}

        {promoteInboundSales ? (
          <div className={yStack({ gap: 'md' })}>
            <OfferSummary />
            <InboundSalesCard />
          </div>
        ) : (
          <OfferSummary />
        )}
      </div>
    </div>
  )
})
OfferPresenter.displayName = 'OfferPresenter'
